/**
 * "Share with mods" - register shared items on OPBridge as named actions.
 *
 * OPMod ask + operator, 2026-09-23 (`OPApps/opbuttons-opmod.md`): mods in
 * other countries press what the streamer shared, via OPBridge's
 * `POST /actions/{id}/fire`. Sharing makes an item GRANTABLE, never granted -
 * the streamer picks which mods may press what in OPMod.
 *
 * Operator, 2026-09-29: *"literally everything"* is shareable. WHAT each kind
 * registers - its list of operations, recorded from the real send code - is
 * `modShare/ops.ts`. This file only pushes that list and withdraws it.
 *
 * ─── Where it registers ───
 *
 * On every bridge the item's own presses land on (`ops.ts::shareTargets`),
 * so a mod's press runs where a finger's would.
 *
 * ─── What OPBridge supports decides what registers ───
 *
 * An operation that needs a shape OPBridge does not advertise yet
 * (`route` / `sequence` / `inputs`, see `ops.ts`) is NOT registered and is
 * counted as waiting, and the Inspector says so. Registering it anyway would
 * be worse than not: an older OPBridge rejects the unknown payload kind, or
 * (for `inputs`) silently drops the value slot and registers a fixed press.
 *
 * ─── Deletes only on an OBSERVED transition, never by sweeping ───
 *
 * An operation is withdrawn (`DELETE`) only when THIS browser registered it
 * (the record survives reloads, see `loadRegistered`) and then sees it go: the switch off, the item deleted, or the operation
 * gone from the list (a grid cell removed). There is deliberately no "list
 * OPBridge's actions and delete what I do not know" sweep: projects are
 * per-browser, so a device lacking a project would withdraw every share
 * another device made - the whole-set hazard that let smokes wipe the
 * operator's pedal rules (CP-152). Cost, stated: something removed while this
 * app was closed stays registered until it is re-shared and switched off.
 *
 * ─── Controllers (DEC-256) ───
 *
 * An item a controller is assigned to (`controllerBound`) registers exactly
 * as a shared one does, because a binding in OPBridge fires a named action:
 * no registration, nothing for the pedal to press. When such an item is
 * DELETED (gone from every project, not merely unshared) its bindings are
 * removed from OPBridge too, so a pedal is never left aimed at an action that
 * no longer exists. Unsharing or unassigning never touches a binding: the
 * Inspector removes those one at a time, with the operator looking.
 */

import { createEffect, createSignal, on } from 'solid-js';
import { projects, type LayoutItem } from '../layout/projectStore.ts';
import { bridges, getConnection } from '../opbridge/store.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { OpbridgeError, type NamedAction, type OpbridgeHttpClient } from '../opbridge/httpClient.ts';
import { unbindActions } from '../opbridge/controllers.ts';
import {
  planShare, shareTargets, unshareableReason as staticReason, actionIdFor, needsOf, NEED_CAPABILITY,
  type Need, type ShareOp,
} from './ops.ts';

const DEBOUNCE_MS = 1500;

export type ShareStatus =
  | { kind: 'off' }
  | { kind: 'unsupported'; why: string }
  | { kind: 'pending' }
  /** `waiting` operations need an OPBridge feature (`waitingOn`) this
   *  bridge does not advertise yet; `notes` say how a mod's press differs
   *  from a finger's, where it does. */
  | { kind: 'shared'; ops: number; waiting: number; waitingOn: Need[]; notes: string[] }
  /** Nothing registered yet because EVERY operation needs an OPBridge
   *  feature this bridge does not advertise. Never shown as "Shared". */
  | { kind: 'waiting'; waiting: number; waitingOn: Need[]; notes: string[] }
  | { kind: 'blocked'; why: string }
  | { kind: 'error'; why: string };

const [statuses, setStatuses] = createSignal<Record<string, ShareStatus>>({});

/** Reactive status for the Inspector. */
export function shareStatus(itemId: string): ShareStatus {
  return statuses()[itemId] ?? { kind: 'off' };
}

function setStatus(itemId: string, s: ShareStatus): void {
  setStatuses((prev) => ({ ...prev, [itemId]: s }));
}

/** Why an item cannot be shared, or null. Same answer the sync uses. */
export function unshareableReason(item: LayoutItem): string | null {
  return staticReason(item);
}

/** 401 = scope not granted; 404 = route not mounted where we reach it. */
function describeFailure(err: unknown): ShareStatus {
  if (err instanceof OpbridgeError && (err.kind === 'unauthorized' || err.httpStatus === 404)) {
    return { kind: 'blocked', why: 'Your OPBridge does not let OPButtons share yet. It needs an OPBridge update and your OK there.' };
  }
  const msg = err instanceof Error ? err.message : String(err);
  return { kind: 'error', why: msg };
}

/** Does this bridge advertise every proposed shape `op` relies on? */
function missingNeeds(op: ShareOp, cfg: BridgeConfig): Need[] {
  const caps = (getConnection(cfg.id)?.health()?.capabilities ?? {}) as Record<string, unknown>;
  return needsOf(op).filter((n) => caps[NEED_CAPABILITY[n]] !== true);
}

/**
 * Per item: what THIS DEVICE registered, per bridge, per operation, with a
 * fingerprint of exactly what was sent - kept across reloads.
 *
 * Why it is persisted and per-operation (OPBridge S290, 2026-09-29): every
 * `PUT /admin/actions/{id}` rewrites and fsyncs OPBridge's whole config
 * (measured there: 270 KB). An OPVisual grid shares ~130 operations, and the
 * first version of this sync, which only remembered within one session,
 * re-registered EVERY operation on every app load. Now an unchanged operation
 * is never re-sent, and editing one grid cell re-sends one cell.
 *
 * Healing: everything is re-sent at most once a day (`REPUSH_MS`), in case
 * OPBridge lost a registration; "Try again" forces it.
 */
interface Registered {
  /** Last time every operation of this item was sent regardless. */
  at: number;
  byBridge: Map<string, Map<string, string>>;
  /** A controller was assigned to it when last synced (DEC-256): its
   *  bindings go when the item is deleted. */
  bound?: boolean;
}
const REGISTERED_KEY = 'opbuttons.modShare.registered.v1';
const REPUSH_MS = 24 * 60 * 60 * 1000;

function loadRegistered(): Map<string, Registered> {
  try {
    const raw = JSON.parse(localStorage.getItem(REGISTERED_KEY) ?? '{}') as
      Record<string, { at: number; byBridge: Record<string, Record<string, string>>; bound?: boolean }>;
    return new Map(Object.entries(raw).map(([id, r]) => [id, {
      at: r.at,
      byBridge: new Map(Object.entries(r.byBridge).map(([b, ops]) => [b, new Map(Object.entries(ops))])),
      ...(r.bound ? { bound: true } : {}),
    }]));
  } catch { return new Map(); }
}

function saveRegistered(m: Map<string, Registered>): void {
  try {
    const out: Record<string, { at: number; byBridge: Record<string, Record<string, string>>; bound?: boolean }> = {};
    for (const [id, r] of m) {
      out[id] = {
        at: r.at,
        byBridge: Object.fromEntries([...r.byBridge].map(([b, ops]) => [b, Object.fromEntries(ops)])),
        ...(r.bound ? { bound: true } : {}),
      };
    }
    localStorage.setItem(REGISTERED_KEY, JSON.stringify(out));
  } catch { /* storage unavailable: falls back to re-registering, as before */ }
}

let registered = new Map<string, Registered>();
let timer: number | null = null;
/** Registrations actually sent by the last sync - for the harness and logs. */
let lastSent = 0;
let running = false;
let rerun = false;

/** Most entries OPBridge takes in one `POST /admin/actions`. */
const BATCH_MAX = 512;

/** Register `actions` in as few config writes as OPBridge allows: one batch
 *  per 512 (OPBridge S290; every single PUT rewrites its whole config, and an
 *  OPVisual grid is ~130 actions). True when every batch landed. False on any
 *  refusal or an OPBridge without the batch route: the caller then falls back
 *  to one PUT each, which is what tells it WHICH action was refused (a batch
 *  is all or nothing). */
async function putBatched(client: OpbridgeHttpClient, actions: NamedAction[]): Promise<boolean> {
  try {
    for (let i = 0; i < actions.length; i += BATCH_MAX) {
      await client.batchNamedActions({ put: actions.slice(i, i + BATCH_MAX) });
    }
    return true;
  } catch {
    return false;
  }
}

async function withdraw(bridgeId: string, actionIds: Iterable<string>): Promise<void> {
  const conn = getConnection(bridgeId);
  if (!conn || conn.status() !== 'connected') return;
  const ids = [...actionIds];
  if (ids.length === 0) return;
  const client = conn.httpClient();
  try {
    // A missing id lands in `missing`, never a refusal, so this is safe to retry.
    for (let i = 0; i < ids.length; i += BATCH_MAX) await client.batchNamedActions({ delete: ids.slice(i, i + BATCH_MAX) });
    return;
  } catch { /* an older OPBridge, or one refused: one at a time, as before */ }
  for (const id of ids) {
    try { await client.deleteNamedAction(id); } catch { /* already gone, or never registered */ }
  }
}

/** Plan and push one item. Returns what it registered, per bridge. */
async function shareOne(item: LayoutItem, prev: Registered | undefined, force: boolean): Promise<Registered | null> {
  const targets = shareTargets(item);
  if (targets.length === 0) { setStatus(item.id, { kind: 'error', why: 'no OPBridge is paired' }); return prev ?? null; }

  const plans: Array<{ cfg: BridgeConfig; ops: ShareOp[]; notes: string[] }> = [];
  for (const cfg of targets) {
    const plan = await planShare(item, cfg);
    if ('unsupported' in plan) { setStatus(item.id, { kind: 'unsupported', why: plan.unsupported }); return prev ?? null; }
    plans.push({ cfg, ...plan });
  }
  // Re-send everything regardless when forced, or once a day (see Registered).
  const repush = force || !prev || Date.now() - prev.at > REPUSH_MS;
  const byBridge = new Map<string, Map<string, string>>();
  let okOps = 0;
  let sent = 0;
  let waiting = 0;
  const waitingOn = new Set<Need>();
  let failure: ShareStatus | null = null;
  const notes = [...new Set(plans.flatMap((p) => p.notes))];

  for (const { cfg, ops } of plans) {
    const conn = getConnection(cfg.id);
    const prevOps = prev?.byBridge.get(cfg.id);
    if (!conn || conn.status() !== 'connected') {
      // Keep what is registered there; do not re-send it all on reconnect.
      if (prevOps) byBridge.set(cfg.id, prevOps);
      failure ??= { kind: 'error', why: 'OPBridge is not connected' };
      continue;
    }
    const client = conn.httpClient();
    const ids = new Map<string, string>();
    const setupDone = new Set<string>();
    // Everything to (re)register here, sent as ONE batch after the loop.
    const toPut: Array<{ id: string; fp: string; action: NamedAction }> = [];
    for (const op of ops) {
      const missing = missingNeeds(op, cfg);
      if (missing.length > 0) { waiting++; missing.forEach((n) => waitingOn.add(n)); continue; }
      const id = actionIdFor(item.id, op.key);
      const fp = JSON.stringify([op.label, op.payload, op.inputs ?? null, op.setup ?? null]);
      if (!repush && prevOps?.get(id) === fp) { ids.set(id, fp); okOps++; continue; }
      // "Sharing..." only when something is actually about to be sent - most
      // syncs now send nothing, and a flash on every one would be noise.
      if (toPut.length === 0) setStatus(item.id, { kind: 'pending' });
      try {
        for (const req of op.setup ?? []) {
          const k = JSON.stringify(req);
          if (setupDone.has(k)) continue;
          setupDone.add(k);
          await client.replay(req);
        }
        toPut.push({
          id, fp,
          action: {
            id, label: op.label, payload: op.payload as NamedAction['payload'],
            ...(op.inputs && op.inputs.length ? { inputs: op.inputs } : {}),
          },
        });
      } catch (err) {
        failure ??= describeFailure(err);
        // The OLD registration is still there: keep it (never withdraw it for
        // a failed update) but mark it so the next sync re-sends it.
        if (prevOps?.has(id)) ids.set(id, 'retry');
      }
    }
    // Setup has run for every operation above; now register them together.
    if (toPut.length > 0 && await putBatched(client, toPut.map((t) => t.action))) {
      for (const t of toPut) ids.set(t.id, t.fp);
      okOps += toPut.length;
      sent += toPut.length;
    } else {
      for (const t of toPut) {
        try {
          await client.putNamedAction(t.id, t.action);
          ids.set(t.id, t.fp);
          okOps++;
          sent++;
        } catch (err) {
          failure ??= describeFailure(err);
          if (prevOps?.has(t.id)) ids.set(t.id, 'retry');
        }
      }
    }
    byBridge.set(cfg.id, ids);
    // Operations this session registered here before and that are no longer
    // in the plan (a deleted grid cell, a renamed section).
    if (prevOps) await withdraw(cfg.id, [...prevOps.keys()].filter((id) => !ids.has(id)));
  }
  // Bridges this item no longer sends to.
  for (const [bridgeId, before] of prev?.byBridge ?? []) {
    if (!byBridge.has(bridgeId)) await withdraw(bridgeId, before.keys());
  }

  if (failure && okOps === 0) setStatus(item.id, failure);
  else if (okOps === 0 && waiting > 0) setStatus(item.id, { kind: 'waiting', waiting, waitingOn: [...waitingOn], notes });
  else setStatus(item.id, { kind: 'shared', ops: okOps, waiting, waitingOn: [...waitingOn], notes });
  lastSent += sent;
  return { at: repush ? Date.now() : prev!.at, byBridge, ...(item.controllerBound ? { bound: true } : {}) };
}

/** Kill switch, same shape as ruleSync's: an automated context cannot be
 *  told from a real device, so the harness declares it. */
const DISABLE_KEY = 'opbuttons.modShareSync.disabled';
function syncDisabled(): boolean {
  try { return localStorage.getItem(DISABLE_KEY) === '1'; } catch { return false; }
}

function allItems(): LayoutItem[] {
  return projects().flatMap((p) => p.pages.flatMap((pg) => pg.items));
}

async function sync(force = false): Promise<void> {
  if (syncDisabled()) return;
  // One sync at a time: two overlapping runs would each see the other's
  // registrations as "not mine" and withdraw them.
  if (running) { rerun = true; return; }
  running = true;
  lastSent = 0;
  try {
    const items = allItems();
    const shared = new Map<string, LayoutItem>();
    for (const item of items) {
      if (!item.shareWithMods && !item.controllerBound) continue;
      const why = unshareableReason(item);
      if (why) { setStatus(item.id, { kind: 'unsupported', why }); continue; }
      shared.set(item.id, item);
    }
    // Withdraw what this device registered and now sees unshared or gone.
    // NEVER on an empty layout: the record now survives reloads, so a sync
    // that ran before projects loaded would otherwise withdraw every share.
    const next = new Map<string, Registered>();
    const present = new Set(items.map((i) => i.id));
    for (const [id, prev] of registered) {
      if (shared.has(id)) continue;
      if (items.length === 0) { next.set(id, prev); continue; }
      // Deleted, and a controller was assigned: take its bindings out of
      // OPBridge first, while the ids still name something (see the header).
      if (prev.bound && !present.has(id)) {
        await unbindActions(new Set([...prev.byBridge.values()].flatMap((ops) => [...ops.keys()])));
      }
      for (const [bridgeId, ids] of prev.byBridge) await withdraw(bridgeId, ids.keys());
      setStatus(id, { kind: 'off' });
    }
    for (const [id, item] of shared) {
      const r = await shareOne(item, registered.get(id), force);
      if (r) next.set(id, r);
    }
    registered = next;
    saveRegistered(registered);
    if (lastSent > 0) console.info(`[modShare] sent ${lastSent} registration(s) to OPBridge`);
  } finally {
    running = false;
    if (rerun) { rerun = false; schedule(); }
  }
}

function schedule(): void {
  if (timer !== null) clearTimeout(timer);
  timer = window.setTimeout(() => { timer = null; void sync(); }, DEBOUNCE_MS);
}

/** Re-try now (the Inspector's "Try again"). */
export function retryShare(): void { void sync(true); }

/** Run a sync now and resolve when it has finished - after any sync already
 *  running. For "Assign controller...", which must not write a binding
 *  before the action it names is registered. */
export async function syncNow(): Promise<void> {
  if (timer !== null) { clearTimeout(timer); timer = null; }
  while (running) await new Promise((r) => setTimeout(r, 50));
  await sync();
}

/** The action ids THIS device has registered for an item, per bridge id,
 *  that landed (not awaiting a retry). Empty when nothing is registered. */
export function registeredActionIds(itemId: string): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const [bridgeId, ops] of registered.get(itemId)?.byBridge ?? []) {
    const ok = [...ops].filter(([, fp]) => fp !== 'retry').map(([id]) => id);
    if (ok.length) out.set(bridgeId, new Set(ok));
  }
  return out;
}

/** Wire the reactive effects. Call once at app boot beside the other
 *  install*Sync() functions. */
export function installModShareSync(): void {
  registered = loadRegistered();
  createEffect(on(projects, () => schedule(), { defer: true }));
  // (Re)connect or a new /health (an OPBridge update can add capabilities):
  // push everything shared, since a bridge that was down missed it.
  createEffect(on(() => bridges().map((b) => {
    const c = getConnection(b.id);
    const caps = (c?.health()?.capabilities ?? {}) as Record<string, unknown>;
    return `${b.id}:${c?.status() ?? ''}:${Object.values(NEED_CAPABILITY).map((k) => (caps[k] === true ? 1 : 0)).join('')}`;
  }).join(','), () => schedule()));
}
